import React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SxProps, Theme, alpha } from '@mui/material/styles';
import COLORS from '../../constants/colors';

export const CARD_BORDER = alpha(COLORS.MAIN_DARK, 0.08);

interface SectionCardProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Right-aligned slot in the header, for buttons. */
  action?: React.ReactNode;
  /** Content that manages its own padding, e.g. a data grid. */
  disablePadding?: boolean;
  /** Let the header fold the card away. Requires a title. */
  collapsible?: boolean;
  /** Only meaningful with `collapsible`. Defaults to open. */
  defaultExpanded?: boolean;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * The single card surface for detail pages. Every screen used its own mix of
 * `p: '24px'` / `padding: 2` / bare grids, so cards never lined up with each
 * other; this fixes the padding, radius and header rhythm in one place.
 */
export function SectionCard({
  title,
  description,
  action,
  disablePadding,
  collapsible,
  defaultExpanded = true,
  children,
  sx,
}: SectionCardProps) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const contentId = React.useId();
  const hasHeader = Boolean(title || action);
  const canCollapse = Boolean(collapsible && title);

  const heading = (
    <Box sx={{ minWidth: 0 }}>
      {title && (
        <Typography
          component="h2"
          sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: '20px', color: COLORS.MAIN_DARK }}
        >
          {title}
        </Typography>
      )}
      {description && (
        <Typography sx={{ fontSize: '0.8125rem', color: COLORS.LIGHT_GRAY, mt: '2px' }}>{description}</Typography>
      )}
    </Box>
  );

  const body = <Box sx={{ p: disablePadding ? 0 : '20px' }}>{children}</Box>;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${CARD_BORDER}`,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {hasHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
            px: '20px',
            py: '14px',
            // With nothing below it, the header should not draw a hanging rule.
            borderBottom: canCollapse && !expanded ? 'none' : `1px solid ${CARD_BORDER}`,
          }}
        >
          {canCollapse ? (
            // Only the heading is the button, so an `action` button is never
            // nested inside another button.
            <Box
              component="button"
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
              aria-controls={contentId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: '1 1 auto',
                minWidth: 0,
                m: 0,
                p: 0,
                border: 0,
                background: 'none',
                font: 'inherit',
                textAlign: 'start',
                cursor: 'pointer',
                color: 'inherit',
                '&:focus-visible': {
                  outline: `2px solid ${COLORS.PRIMARY_BLUE}`,
                  outlineOffset: '2px',
                  borderRadius: '4px',
                },
              }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: '20px',
                  flexShrink: 0,
                  color: alpha(COLORS.MAIN_DARK, 0.55),
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                }}
              />
              {heading}
            </Box>
          ) : (
            heading
          )}
          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Box>
      )}

      {canCollapse ? (
        // Children stay mounted while collapsed so form state and validation
        // are never silently dropped.
        <Collapse in={expanded} timeout="auto">
          <Box id={contentId}>{body}</Box>
        </Collapse>
      ) : (
        body
      )}
    </Paper>
  );
}
